import { Type_RefFuncKrpano } from '@components/Krpano/types';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { I_Group, I_Hotspot, I_Scene } from '@containers/Home/types';
import { useLeadFormCapture } from '@containers/Home/hook/useLeadFormCapture';
import { useTourEventState, useTourStats } from '../global/TourStats';
import {
  selectConfigAutoChangeScene,
  selectConfigAutoRotate,
  selectConfigMap,
  selectDetailFirstScene,
  selectDetailProject,
  selectListFloorplan,
  selectListGroup,
  selectSceneSelected,
} from '../selectors';
import { actions } from '../slice';

interface Props {
  krpanoRef?: Type_RefFuncKrpano;
}

export const useKrpano = ({ krpanoRef }: Props) => {
  const dispatch = useDispatch();
  const configAutoRotate = useSelector(selectConfigAutoRotate);
  const configAutoChangeScene = useSelector(selectConfigAutoChangeScene);
  const detailFirstScene = useSelector(selectDetailFirstScene);
  const detailProject = useSelector(selectDetailProject);
  const listGroup = useSelector(selectListGroup);
  const sceneSelected = useSelector(selectSceneSelected);
  const listFloorplan = useSelector(selectListFloorplan);
  const configMap = useSelector(selectConfigMap);

  const [, setTourStats] = useTourStats('stats');
  const [tourEventStats, setTourEventState] = useTourEventState('state');
  const { onCheckShowFormCaptureOnHotspot } = useLeadFormCapture({});
  const configAutoChangeSceneRef = useRef(null!);

  const isExistSound = useMemo(
    () =>
      sceneSelected?.hotspots?.some(
        item =>
          (item.type === 'sound' &&
            (item.config?.sound?.file || item.config?.sound?.url)) ||
          (item.type === 'instructor' && item.config?.instructor?.media) ||
          (item.type === 'media' &&
            item.config?.media?.type === 'media-video' &&
            item.config?.media?.media?.src),
      ) ||
      !!detailProject?.background_sound ||
      !!sceneSelected?.background_sound,
    [detailProject, sceneSelected],
  );

  const isShowIconInfoPlace = useMemo(
    () => !!detailProject?.post,
    [detailProject],
  );

  const isShowIconInfoScene = useMemo(
    () => !!sceneSelected?.post,
    [sceneSelected],
  );

  const isShowIconMap = useMemo(() => !!configMap?.isEnabled, [configMap]);

  const isShowIconFloorplan = useMemo(
    () => !!listFloorplan.length,
    [listFloorplan],
  );

  const isShowIconMultipleStaging = useMemo(
    () =>
      !!(
        sceneSelected?.mode === 'staging' &&
        sceneSelected?.staging?.length &&
        sceneSelected?.staging?.filter(item => item.media).length &&
        !!sceneSelected?.configStaging?.modeDisplay
      ),
    [sceneSelected],
  );

  const isShowIconSoundProject = useMemo(() => isExistSound, [isExistSound]);

  const isShowIconSoundScene = useMemo(
    () => !!sceneSelected?.background_sound,
    [sceneSelected],
  );

  // NOTE: play change scene
  const isShowIconChangeScene = useMemo(
    () => configAutoChangeScene.enabled,
    [configAutoChangeScene],
  );
  const isChangeScene = useMemo(
    () => configAutoChangeScene.on,
    [configAutoChangeScene],
  );

  // NOTE: rotate
  const isShowIconRotate = useMemo(
    () => configAutoRotate.enabled,
    [configAutoRotate],
  );
  const isRotate = useMemo(() => configAutoRotate.on, [configAutoRotate]);
  const waitTimeAutoRotate = useMemo(
    () => configAutoRotate.waitTime,
    [configAutoRotate],
  );
  const speedAutoRotate = useMemo(
    () => configAutoRotate.speed,
    [configAutoRotate],
  );

  useEffect(() => {
    configAutoChangeSceneRef.current = configAutoChangeScene;
  }, [configAutoChangeScene]);

  const onClickHotspot = useCallback(
    ({ hotspot }) => onCheckShowFormCaptureOnHotspot(hotspot),
    [onCheckShowFormCaptureOnHotspot],
  );

  const onChangeAutoChangeScene = useCallback(
    on => {
      if (
        configAutoChangeSceneRef.current?.enabled &&
        configAutoChangeSceneRef.current?.on !== on
      )
        dispatch(actions.setConfigAutoChangeScene({ on }));
    },
    [dispatch],
  );

  const onToFirstScene = useCallback(() => {
    dispatch(actions.setSceneSelected(detailFirstScene));
  }, [detailFirstScene, dispatch]);

  // NOTE: event krpano controlbar
  const onUp = useCallback(() => {
    setTourStats(prev => ({ ...prev, btnDirectionActive: 'top' }));
    krpanoRef?.toUp();
  }, [krpanoRef, setTourStats]);

  const onLeft = useCallback(() => {
    setTourStats(prev => ({ ...prev, btnDirectionActive: 'left' }));
    krpanoRef?.toLeft();
  }, [krpanoRef, setTourStats]);

  const onDown = useCallback(() => {
    setTourStats(prev => ({ ...prev, btnDirectionActive: 'bottom' }));
    krpanoRef?.toDown();
  }, [krpanoRef, setTourStats]);

  const onRight = useCallback(() => {
    setTourStats(prev => ({ ...prev, btnDirectionActive: 'right' }));
    krpanoRef?.toRight();
  }, [krpanoRef, setTourStats]);

  const onVR = useCallback(() => krpanoRef?.viewWebvr(), [krpanoRef]);

  const onViewNormal = useCallback(() => {
    krpanoRef?.viewNormal();
  }, [krpanoRef]);

  const onViewPlannet = useCallback(() => {
    krpanoRef?.viewPlanet();
  }, [krpanoRef]);

  const onViewMirrorBall = useCallback(() => {
    krpanoRef?.viewMirrorBall();
  }, [krpanoRef]);

  const onToggleGyro = useCallback(() => {
    krpanoRef?.toggleGyro();
  }, [krpanoRef]);

  const onRemoveAllChevronHotspot = useCallback(
    (hotspots: I_Hotspot[]) => krpanoRef?.removeAllChevronHotspot(hotspots),
    [krpanoRef],
  );

  const onStopRotate = useCallback(() => {
    dispatch(actions.setConfigAutoRotate({ on: false }));
    krpanoRef?.stopAutoRotate();
  }, [dispatch, krpanoRef]);

  const onStartRotate = useCallback(() => {
    dispatch(actions.setConfigAutoRotate({ on: true }));
    krpanoRef?.startAutoRotate({
      speed: speedAutoRotate,
      wait_time: waitTimeAutoRotate,
    });
  }, [dispatch, krpanoRef, speedAutoRotate, waitTimeAutoRotate]);

  const onToggleRotate = useCallback(() => {
    if (onStartRotate && onStopRotate)
      if (!isRotate) onStartRotate();
      else onStopRotate();
  }, [isRotate, onStartRotate, onStopRotate]);

  const onStopVertical = useCallback(() => {
    setTourStats(prev => ({ ...prev, btnDirectionActive: '' }));
    krpanoRef?.stopToVertical();
  }, [krpanoRef, setTourStats]);

  const onStopHorizontal = useCallback(() => {
    setTourStats(prev => ({ ...prev, btnDirectionActive: '' }));
    krpanoRef?.stopToHorizontal();
  }, [krpanoRef, setTourStats]);

  const onFullscreen = useCallback(() => {
    krpanoRef?.viewFullscreen();
  }, [krpanoRef]);

  const onTurnOnScreenshotCapture = useCallback(() => {
    krpanoRef?.call('show_snapshot(true)');
  }, [krpanoRef]);

  // NOTE: sound hotspot
  const onStartHotspotSound = useCallback(() => {
    krpanoRef?.triggerSound();
  }, [krpanoRef]);

  const onStopHotspotSound = useCallback(() => {
    krpanoRef?.destroySound();
  }, [krpanoRef]);

  const onUnmuteHotspotSound = useCallback(() => {
    krpanoRef?.unmuteAllSound(sceneSelected);
  }, [krpanoRef, sceneSelected]);

  const onMuteHotspotSound = useCallback(() => {
    krpanoRef?.muteAllSound(sceneSelected);
  }, [krpanoRef, sceneSelected]);

  // NOTE: sound scene
  const onSetSoundOnScene = useCallback(
    isSoundOnScene =>
      setTourEventState(prev => ({
        ...prev,
        isSoundOnScene,
      })),
    [setTourEventState],
  );

  // NOTE: mode multiple staging
  const onToggleModeMultipleStaging = useCallback(
    (active: boolean = undefined) => {
      setTourEventState(prev => ({
        ...prev,
        isActiveModeMultipleStaging:
          active === undefined ? !prev.isActiveModeMultipleStaging : active,
      }));
    },
    [setTourEventState],
  );

  const onTurnOnSoundScene = useCallback(
    () => onSetSoundOnScene(true),
    [onSetSoundOnScene],
  );

  const onTurnOffSoundScene = useCallback(
    () => onSetSoundOnScene(false),
    [onSetSoundOnScene],
  );

  const onToggleSoundScene = useCallback(() => {
    if (onTurnOffSoundScene && onTurnOnSoundScene)
      if (!tourEventStats.isSoundOnScene) onTurnOnSoundScene();
      else onTurnOffSoundScene();
  }, [onTurnOffSoundScene, onTurnOnSoundScene, tourEventStats.isSoundOnScene]);

  // NOTE: sound project
  const onTurnOnSoundProject = useCallback(() => {
    onUnmuteHotspotSound();
    dispatch(actions.setConfigSoundProject({ on: true }));
  }, [dispatch, onUnmuteHotspotSound]);

  const onTurnOffSoundProject = useCallback(() => {
    onMuteHotspotSound();
    dispatch(actions.setConfigSoundProject({ on: false }));
  }, [dispatch, onMuteHotspotSound]);

  // NOTE: info place tour
  const onOpenDialogPostProject = useCallback(() => {
    setTourEventState(prev => ({ ...prev, visibleDialogPostProject: true }));
  }, [setTourEventState]);

  const onCloseDialogPostProject = useCallback(() => {
    setTourEventState(prev => ({ ...prev, visibleDialogPostProject: false }));
  }, [setTourEventState]);

  const onShowInfoPlace = useCallback(() => {
    setTourEventState(prev => ({
      ...prev,
      idPostProjectSelected: detailProject?.post,
    }));
    onOpenDialogPostProject();
  }, [detailProject, onOpenDialogPostProject, setTourEventState]);

  // NOTE: info post scene
  const onOpenDialogPostScene = useCallback(() => {
    setTourEventState(prev => ({ ...prev, visibleDialogPostScene: true }));
  }, [setTourEventState]);

  const onCloseDialogPostScene = useCallback(() => {
    setTourEventState(prev => ({
      ...prev,
      visibleDialogPostScene: false,
    }));
  }, [setTourEventState]);

  const onToggleInfoScene = useCallback(
    (isVisible = undefined) => {
      if (sceneSelected?.post) {
        if (
          (isVisible !== undefined && isVisible) ||
          !tourEventStats.visibleDialogPostScene
        ) {
          setTourEventState(prev => ({
            ...prev,
            idPostSceneSelected: sceneSelected?.post,
          }));
          onOpenDialogPostScene();
        } else if (
          (isVisible !== undefined && !isVisible) ||
          tourEventStats.visibleDialogPostScene
        )
          onCloseDialogPostScene();
      } else onCloseDialogPostScene();
    },
    [
      onCloseDialogPostScene,
      onOpenDialogPostScene,
      setTourEventState,
      tourEventStats,
      sceneSelected,
    ],
  );

  const onToggleFloorplan = useCallback(() => {
    setTourEventState(prev => ({
      ...prev,
      isVisibleFloorplan: !prev.isVisibleFloorplan,
    }));
  }, [setTourEventState]);

  const onToggleMap = useCallback(() => {
    setTourEventState(prev => ({
      ...prev,
      isVisibleMap: !prev.isVisibleMap,
    }));
  }, [setTourEventState]);

  // UTILS

  const onGenerateXMLMenuThumbnailOnVR = useCallback(() => {
    let xml = '<krpano thumbready="0">';

    function generateSceneXml(scene: I_Scene, group: I_Group = undefined) {
      return `<scene name="scene_${scene.id}" title="${scene.title
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll(`'`, '&apos;')
        .replaceAll('"', '&quot;')}" onstart="set(sceneId, '${
        scene.id
      }'); events.dispatch('onnewpanovr');" thumburl="${
        scene.media.src_thumb || scene.media.src_preview
      }" lat="" lng="" alt="" heading="" group="${
        group ? 'group_' + group?.id : ``
      }" nogrid="false"/>`;
    }

    if (listGroup.length === 1 && listGroup[0].id === 'uncategory') {
      listGroup[0].scenes.forEach(scene => {
        xml += generateSceneXml(scene);
      });
    } else {
      xml += ``;
      listGroup.forEach(group => {
        // xml += `<panoramagroup name="group_${group.id}" title="Title: ${group.name}" description="" mainscene="${group.scenes[0].id}" thumburl="${group.scenes[0].media?.src_thumb}" />`;
        group.scenes.forEach(scene => {
          xml += generateSceneXml(scene);
        });
      });
    }
    xml += '</krpano>';

    return xml;
  }, [listGroup]);

  return useMemo(
    () => ({
      onUp,
      onDown,
      onLeft,
      onRight,
      onVR,
      onToggleGyro,
      onViewNormal,
      onViewPlannet,
      onViewMirrorBall,
      onStopVertical,
      onStopHorizontal,
      onFullscreen,
      onToFirstScene,
      onTurnOnScreenshotCapture,
      onRemoveAllChevronHotspot,
      // rotate
      isRotate,
      onStopRotate,
      onToggleRotate,
      onStartRotate,
      isShowIconRotate,
      //
      onClickHotspot,
      // auto change scene
      isChangeScene,
      isShowIconChangeScene,
      onChangeAutoChangeScene,
      // info tour
      onShowInfoPlace,
      onOpenDialogPostProject,
      onCloseDialogPostProject,
      isShowIconInfoPlace,
      // info tour
      onToggleInfoScene,
      onOpenDialogPostScene,
      onCloseDialogPostScene,
      isShowIconInfoScene,
      // sound
      isExistSound,
      // sound scene
      onSetSoundOnScene,
      onTurnOnSoundScene,
      onToggleSoundScene,
      onTurnOffSoundScene,
      isShowIconSoundScene,
      // sound hotspot
      onStartHotspotSound,
      onStopHotspotSound,
      // sound project
      onTurnOnSoundProject,
      onTurnOffSoundProject,
      isShowIconSoundProject,
      // map
      isShowIconMap,
      onToggleMap,
      // floorplan
      isShowIconFloorplan,
      onToggleFloorplan,
      // multiple staging
      isShowIconMultipleStaging,
      onToggleModeMultipleStaging,
      //
      onGenerateXMLMenuThumbnailOnVR,
    }),
    [
      onUp,
      onDown,
      onLeft,
      onRight,
      onVR,
      onToggleGyro,
      onViewNormal,
      onViewPlannet,
      onViewMirrorBall,
      onStopVertical,
      onStopHorizontal,
      onFullscreen,
      onToFirstScene,
      onTurnOnScreenshotCapture,
      onRemoveAllChevronHotspot,
      isRotate,
      onStopRotate,
      onToggleRotate,
      onStartRotate,
      isShowIconRotate,
      onClickHotspot,
      isChangeScene,
      isShowIconChangeScene,
      onChangeAutoChangeScene,
      onShowInfoPlace,
      onOpenDialogPostProject,
      onCloseDialogPostProject,
      isShowIconInfoPlace,
      onToggleInfoScene,
      onOpenDialogPostScene,
      onCloseDialogPostScene,
      isShowIconInfoScene,
      isExistSound,
      onSetSoundOnScene,
      onTurnOnSoundScene,
      onToggleSoundScene,
      onTurnOffSoundScene,
      isShowIconSoundScene,
      onStartHotspotSound,
      onStopHotspotSound,
      onTurnOnSoundProject,
      onTurnOffSoundProject,
      isShowIconSoundProject,
      isShowIconMap,
      onToggleMap,
      isShowIconFloorplan,
      onToggleFloorplan,
      isShowIconMultipleStaging,
      onToggleModeMultipleStaging,
      onGenerateXMLMenuThumbnailOnVR,
    ],
  );
};
